"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMenu, FiX } from "react-icons/fi";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useLenis } from "../hooks/LenisProvider";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();
  const lenis = useLenis();

  const { t, i18n } = useTranslation("navbar");
  const { t: tLang } = useTranslation("navbar.languages");

  const navLinks = [
    { href: "#about", label: t("links.about") },
    { href: "#service", label: t("links.service") },
    { href: "#products", label: t("links.products") },
    { href: "/request", label: t("links.catalog") },
    { href: "#contact", label: t("links.contact") },
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSmoothScroll = async (e, href) => {
    e.preventDefault();
    setMenuOpen(false);

    const isAnchor = href.startsWith("#");

    if (!isAnchor) {
      router.push(href);
      return;
    }

    const hash = href;
    const localePrefix =
      router.locale === i18n.defaultLocale ? "" : `/${router.locale}`;

    // Если мы не на главной — перейти на главную с хешем
    if (router.pathname !== "/" && router.pathname !== "/[...slug]") {
      await router.push(`${localePrefix}/${hash}`);
      return;
    }

    // Если уже на главной — просто скроллим
    const id = hash.replace("#", "");
    const el = document.getElementById(id);

    if (el && lenis) {
      lenis.scrollTo(el, {
        offset: -64,
        duration: 1.2,
        easing: (t) => 1 - Math.pow(1 - t, 3),
      });
      window.history.pushState(null, "", hash);
    } else {
      window.history.pushState(null, "", hash);
    }
  };

  const handleLanguageChange = (e, lang) => {
    e.preventDefault();
    setMenuOpen(false);

    const { pathname, query, asPath } = router;

    const hashIndex = asPath.indexOf("#");
    const hash = hashIndex >= 0 ? asPath.slice(hashIndex) : "";

    router.push({ pathname, query }, pathname + hash, { locale: lang });
  };

  return (
    <nav className="fixed top-0 left-0 w-full px-6 py-4 lg:px-10 lg:py-5 flex justify-between items-center z-50 h-16 shadow-lg bg-white">
      <Link
        href="/"
        scroll={false}
        onClick={(e) => handleSmoothScroll(e, "/")}
        className="text-2xl lg:text-3xl font-extrabold tracking-wide font-montserrat cursor-pointer"
      >
        REEFLI
        <span className="align-super text-[0.65em] font-sans ml-0.5">™</span>
      </Link>

      <div className="hidden lg:flex items-center gap-10 text-lg font-medium font-[Futura]">
        <div className="flex gap-10">
          {navLinks.map(({ href, label }, idx) => (
            <a
              key={idx}
              href={href}
              onClick={(e) => handleSmoothScroll(e, href)}
              className="relative group transition-all duration-300 cursor-pointer"
            >
              {label}
              <span className="absolute left-0 -bottom-1 w-0 h-[1px] bg-[#262104] group-hover:w-full transition-all duration-300"></span>
            </a>
          ))}
        </div>

        <div className="ml-10 flex items-center gap-2 text-sm font-light tracking-wider">
          <a
            href="#"
            onClick={(e) => handleLanguageChange(e, "de")}
            className="relative group cursor-pointer px-1"
          >
            {tLang("DE")}
            <span className="absolute left-0 -bottom-1 w-0 h-[1px] bg-[#262104] group-hover:w-full transition-all duration-300"></span>
          </a>
          <span>|</span>
          <a
            href="#"
            onClick={(e) => handleLanguageChange(e, "en")}
            className="relative group cursor-pointer px-1"
          >
            {tLang("EN")}
            <span className="absolute left-0 -bottom-1 w-0 h-[1px] bg-[#262104] group-hover:w-full transition-all duration-300"></span>
          </a>
        </div>
      </div>

      <div className="lg:hidden z-50">
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          {menuOpen ? <FiX size={26} /> : <FiMenu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && isMobile && (
          <motion.div
            initial={{ y: "-100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "-100%", opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute top-0 left-0 w-full h-screen bg-reefliBeige flex flex-col items-center justify-center gap-8 text-xl font-[Futura] overflow-y-auto"
          >
            {navLinks.map(({ href, label }, idx) => (
              <motion.a
                key={idx}
                href={href}
                onClick={(e) => handleSmoothScroll(e, href)}
                whileHover={{ scale: 1.1 }}
                className="cursor-pointer"
              >
                {label}
              </motion.a>
            ))}

            <div className="flex items-center gap-2 text-sm font-light tracking-wider">
              <a href="#" onClick={(e) => handleLanguageChange(e, "de")}>
                <span className="relative group cursor-pointer px-1">
                  {tLang("DE")}
                </span>
              </a>
              <span>|</span>
              <a href="#" onClick={(e) => handleLanguageChange(e, "en")}>
                <span className="relative group cursor-pointer px-1">
                  {tLang("EN")}
                </span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
